import React, { useState, useEffect, useRef } from 'react';
import { Search, Star, Archive, Trash2, MoreVertical } from 'lucide-react';
import { itemAPI } from '../../services/api';

const ItemsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', category: '', condition: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionMenu, setActionMenu] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActionMenu(null);
      }
    };

    if (actionMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenu]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const params = { page, search, ...filters };
      const response = await itemAPI.getItems(params);

      if (response.data.success) {
        setItems(response.data.data);
        setTotalPages(response.data.pages);
      }
    } catch (err) {
      console.error('Error loading items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, itemId) => {
    try {
      let response;
      switch (action) {
        case 'feature':
          response = await itemAPI.featureItem(itemId);
          break;
        case 'archive':
          response = await itemAPI.archiveItem(itemId);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this item? This will mark it as deleted.')) {
            response = await itemAPI.deleteItem(itemId);
            // Immediately remove from UI
            if (response?.data.success) {
              setItems(prevItems => prevItems.filter(item => item._id !== itemId));
              setActionMenu(null);
              return; // Skip loadItems() since we already updated the UI
            }
          } else {
            setActionMenu(null);
            return; // User canceled
          }
          break;
        default:
          break;
      }

      if (response?.data.success) {
        await loadItems();
        setActionMenu(null);
      }
    } catch (err) {
      console.error('Action error:', err);
      alert(err.response?.data?.error || `Failed to ${action} item. Please try again.`);
      setActionMenu(null);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Items Management</h1>

      {/* Search and Filters */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
          <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--textSecondary)' }} />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 42px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="sold">Sold</option>
          <option value="pending">Pending</option>
          <option value="archived">Archived</option>
          <option value="deleted">Deleted</option>
        </select>

        <select
          value={filters.condition}
          onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
          style={{
            padding: '12px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="">All Conditions</option>
          <option value="new">New</option>
          <option value="like-new">Like New</option>
          <option value="good">Good</option>
          <option value="fair">Fair</option>
        </select>
      </div>

      {/* Items Table */}
      <div style={{ background: 'var(--cardBackground)', borderRadius: '8px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--background)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Item</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Price</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Condition</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Seller</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Views</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    Loading items...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--textSecondary)' }}>
                    No items found
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '8px',
                          background: 'var(--secondary)',
                          overflow: 'hidden',
                        }}>
                          {item.images?.[0]?.url ? (
                            <img src={item.images[0].url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              No Image
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', marginBottom: '4px' }}>{item.title}</div>
                          {item.isFeatured && <Star size={14} fill="var(--warning)" color="var(--warning)" />}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600' }}>${item.price}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: 'var(--secondary)',
                        color: 'var(--text)',
                      }}>
                        {item.condition}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        background: getStatusColor(item.status),
                        color: 'white',
                      }}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{item.user?.username || 'Unknown'}</td>
                    <td style={{ padding: '12px', color: 'var(--textSecondary)' }}>{item.views || 0}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ position: 'relative' }} ref={actionMenu === item._id ? dropdownRef : null}>
                        <button
                          onClick={() => setActionMenu(actionMenu === item._id ? null : item._id)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                        >
                          <MoreVertical size={16} />
                        </button>

                        {actionMenu === item._id && (
                          <div style={{
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            marginTop: '4px',
                            background: 'var(--cardBackground)',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 10,
                            minWidth: '150px',
                          }}>
                            <button
                              onClick={() => handleAction('feature', item._id)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Star size={16} />
                              {item.isFeatured ? 'Unfeature' : 'Feature'}
                            </button>

                            <button
                              onClick={() => handleAction('archive', item._id)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Archive size={16} />
                              {item.status === 'archived' ? 'Unarchive' : 'Archive'}
                            </button>

                            <button
                              onClick={() => handleAction('delete', item._id)}
                              style={{
                                width: '100%',
                                padding: '10px 16px',
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: 'var(--error)',
                                borderTop: '1px solid var(--border)',
                              }}
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', gap: '8px', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                background: page === 1 ? 'var(--secondary)' : 'var(--primary)',
                color: page === 1 ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              style={{
                padding: '8px 16px',
                background: page === totalPages ? 'var(--secondary)' : 'var(--primary)',
                color: page === totalPages ? 'var(--textSecondary)' : 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: page === totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const getStatusColor = (status) => {
  const colors = {
    draft: '#9b59b6',
    active: '#0fa573',
    sold: '#0076d3',
    pending: '#f5a623',
    archived: '#767676',
    deleted: '#e74c3c',
  };
  return colors[status] || '#767676';
};

export default ItemsTab;
